// Enriched ion data with molar mass, common compounds, and flame test colors

export interface Ion {
  formula: string;
  name: string;
  charge: number;
  type: "cation" | "anion";
  molarMass?: number;
  commonCompounds?: string[];
  flameColor?: string; // CSS color
  flameColorName?: string;
}

export const cations: Ion[] = [
  { formula: "H⁺", name: "Hydrogen", charge: 1, type: "cation", molarMass: 1.008 },
  { formula: "Li⁺", name: "Lithium", charge: 1, type: "cation", molarMass: 6.941, flameColor: "#DC143C", flameColorName: "Crimson red", commonCompounds: ["LiCl", "Li₂CO₃", "LiOH"] },
  { formula: "Na⁺", name: "Sodium", charge: 1, type: "cation", molarMass: 22.99, flameColor: "#FFD700", flameColorName: "Bright yellow", commonCompounds: ["NaCl", "NaOH", "Na₂CO₃", "NaHCO₃"] },
  { formula: "K⁺", name: "Potassium", charge: 1, type: "cation", molarMass: 39.098, flameColor: "#DDA0DD", flameColorName: "Lilac/violet", commonCompounds: ["KCl", "KOH", "KNO₃", "K₂CO₃"] },
  { formula: "Rb⁺", name: "Rubidium", charge: 1, type: "cation", molarMass: 85.468, flameColor: "#FF4500", flameColorName: "Red-violet" },
  { formula: "Cs⁺", name: "Cesium", charge: 1, type: "cation", molarMass: 132.905, flameColor: "#4169E1", flameColorName: "Blue-violet" },
  { formula: "NH₄⁺", name: "Ammonium", charge: 1, type: "cation", molarMass: 18.039, commonCompounds: ["NH₄Cl", "NH₄NO₃", "(NH₄)₂SO₄"] },
  { formula: "Ag⁺", name: "Silver", charge: 1, type: "cation", molarMass: 107.868, commonCompounds: ["AgNO₃", "AgCl", "Ag₂O"] },
  { formula: "Cu⁺", name: "Copper(I)", charge: 1, type: "cation", molarMass: 63.546, flameColor: "#00CED1", flameColorName: "Blue-green" },
  { formula: "Mg²⁺", name: "Magnesium", charge: 2, type: "cation", molarMass: 24.305, commonCompounds: ["MgCl₂", "MgO", "MgSO₄", "Mg(OH)₂"] },
  { formula: "Ca²⁺", name: "Calcium", charge: 2, type: "cation", molarMass: 40.078, flameColor: "#FF4500", flameColorName: "Orange-red", commonCompounds: ["CaCl₂", "CaCO₃", "Ca(OH)₂", "CaSO₄"] },
  { formula: "Sr²⁺", name: "Strontium", charge: 2, type: "cation", molarMass: 87.62, flameColor: "#FF0000", flameColorName: "Bright red", commonCompounds: ["SrCl₂", "SrCO₃"] },
  { formula: "Ba²⁺", name: "Barium", charge: 2, type: "cation", molarMass: 137.327, flameColor: "#7CFC00", flameColorName: "Yellow-green", commonCompounds: ["BaCl₂", "BaSO₄", "Ba(NO₃)₂"] },
  { formula: "Zn²⁺", name: "Zinc", charge: 2, type: "cation", molarMass: 65.38, commonCompounds: ["ZnCl₂", "ZnO", "ZnSO₄"] },
  { formula: "Fe²⁺", name: "Iron(II)", charge: 2, type: "cation", molarMass: 55.845, commonCompounds: ["FeCl₂", "FeSO₄", "FeO"] },
  { formula: "Cu²⁺", name: "Copper(II)", charge: 2, type: "cation", molarMass: 63.546, flameColor: "#00CED1", flameColorName: "Blue-green", commonCompounds: ["CuSO₄", "CuCl₂", "Cu(NO₃)₂"] },
  { formula: "Ni²⁺", name: "Nickel(II)", charge: 2, type: "cation", molarMass: 58.693, commonCompounds: ["NiCl₂", "NiSO₄"] },
  { formula: "Co²⁺", name: "Cobalt(II)", charge: 2, type: "cation", molarMass: 58.933, commonCompounds: ["CoCl₂", "CoSO₄"] },
  { formula: "Mn²⁺", name: "Manganese(II)", charge: 2, type: "cation", molarMass: 54.938, commonCompounds: ["MnCl₂", "MnSO₄", "MnO₂"] },
  { formula: "Sn²⁺", name: "Tin(II)", charge: 2, type: "cation", molarMass: 118.71, commonCompounds: ["SnCl₂", "SnO"] },
  { formula: "Pb²⁺", name: "Lead(II)", charge: 2, type: "cation", molarMass: 207.2, commonCompounds: ["PbCl₂", "Pb(NO₃)₂", "PbSO₄"] },
  { formula: "Hg₂²⁺", name: "Mercury(I)", charge: 2, type: "cation", molarMass: 401.18 },
  { formula: "Hg²⁺", name: "Mercury(II)", charge: 2, type: "cation", molarMass: 200.592, commonCompounds: ["HgCl₂", "HgO"] },
  { formula: "Al³⁺", name: "Aluminium", charge: 3, type: "cation", molarMass: 26.982, commonCompounds: ["AlCl₃", "Al₂O₃", "Al(OH)₃", "Al₂(SO₄)₃"] },
  { formula: "Fe³⁺", name: "Iron(III)", charge: 3, type: "cation", molarMass: 55.845, commonCompounds: ["FeCl₃", "Fe₂O₃", "Fe(NO₃)₃"] },
  { formula: "Cr³⁺", name: "Chromium(III)", charge: 3, type: "cation", molarMass: 51.996, commonCompounds: ["CrCl₃", "Cr₂O₃"] },
];

export const anions: Ion[] = [
  { formula: "F⁻", name: "Fluoride", charge: -1, type: "anion", molarMass: 18.998, commonCompounds: ["NaF", "CaF₂"] },
  { formula: "Cl⁻", name: "Chloride", charge: -1, type: "anion", molarMass: 35.45, commonCompounds: ["NaCl", "KCl", "HCl"] },
  { formula: "Br⁻", name: "Bromide", charge: -1, type: "anion", molarMass: 79.904, commonCompounds: ["NaBr", "KBr"] },
  { formula: "I⁻", name: "Iodide", charge: -1, type: "anion", molarMass: 126.904, commonCompounds: ["KI", "NaI"] },
  { formula: "OH⁻", name: "Hydroxide", charge: -1, type: "anion", molarMass: 17.007, commonCompounds: ["NaOH", "KOH", "Ca(OH)₂"] },
  { formula: "NO₃⁻", name: "Nitrate", charge: -1, type: "anion", molarMass: 62.004, commonCompounds: ["NaNO₃", "KNO₃", "AgNO₃"] },
  { formula: "NO₂⁻", name: "Nitrite", charge: -1, type: "anion", molarMass: 46.005, commonCompounds: ["NaNO₂"] },
  { formula: "ClO₃⁻", name: "Chlorate", charge: -1, type: "anion", molarMass: 83.45, commonCompounds: ["KClO₃"] },
  { formula: "ClO₄⁻", name: "Perchlorate", charge: -1, type: "anion", molarMass: 99.45, commonCompounds: ["KClO₄", "NaClO₄"] },
  { formula: "CH₃COO⁻", name: "Acetate", charge: -1, type: "anion", molarMass: 59.044, commonCompounds: ["CH₃COONa", "CH₃COOK"] },
  { formula: "MnO₄⁻", name: "Permanganate", charge: -1, type: "anion", molarMass: 118.936, commonCompounds: ["KMnO₄"] },
  { formula: "HCO₃⁻", name: "Bicarbonate", charge: -1, type: "anion", molarMass: 61.016, commonCompounds: ["NaHCO₃"] },
  { formula: "HSO₄⁻", name: "Bisulfate", charge: -1, type: "anion", molarMass: 97.07 },
  { formula: "CN⁻", name: "Cyanide", charge: -1, type: "anion", molarMass: 26.018, commonCompounds: ["KCN", "NaCN"] },
  { formula: "O²⁻", name: "Oxide", charge: -2, type: "anion", molarMass: 15.999, commonCompounds: ["Na₂O", "CaO", "Fe₂O₃"] },
  { formula: "S²⁻", name: "Sulfide", charge: -2, type: "anion", molarMass: 32.06, commonCompounds: ["Na₂S", "FeS", "ZnS"] },
  { formula: "SO₄²⁻", name: "Sulfate", charge: -2, type: "anion", molarMass: 96.06, commonCompounds: ["Na₂SO₄", "CuSO₄", "MgSO₄"] },
  { formula: "SO₃²⁻", name: "Sulfite", charge: -2, type: "anion", molarMass: 80.06, commonCompounds: ["Na₂SO₃"] },
  { formula: "CO₃²⁻", name: "Carbonate", charge: -2, type: "anion", molarMass: 60.008, commonCompounds: ["Na₂CO₃", "CaCO₃", "K₂CO₃"] },
  { formula: "CrO₄²⁻", name: "Chromate", charge: -2, type: "anion", molarMass: 115.99, commonCompounds: ["K₂CrO₄", "Na₂CrO₄"] },
  { formula: "Cr₂O₇²⁻", name: "Dichromate", charge: -2, type: "anion", molarMass: 215.99, commonCompounds: ["K₂Cr₂O₇"] },
  { formula: "C₂O₄²⁻", name: "Oxalate", charge: -2, type: "anion", molarMass: 88.02, commonCompounds: ["Na₂C₂O₄"] },
  { formula: "HPO₄²⁻", name: "Hydrogen phosphate", charge: -2, type: "anion", molarMass: 95.97 },
  { formula: "PO₄³⁻", name: "Phosphate", charge: -3, type: "anion", molarMass: 94.97, commonCompounds: ["Na₃PO₄", "Ca₃(PO₄)₂"] },
];

// Solubility rules
export type Solubility = "S" | "I" | "sS";

export function getSolubility(cation: Ion, anion: Ion): Solubility {
  const cat = cation.name;
  const an = anion.name;

  const alwaysSolubleCations = ["Lithium", "Sodium", "Potassium", "Rubidium", "Cesium", "Ammonium"];
  if (alwaysSolubleCations.includes(cat)) return "S";

  const alwaysSolubleAnions = ["Nitrate", "Nitrite", "Acetate", "Perchlorate", "Bicarbonate", "Bisulfate"];
  if (alwaysSolubleAnions.includes(an)) return "S";

  const halides = ["Chloride", "Bromide", "Iodide"];
  const halideExceptions = ["Silver", "Lead(II)", "Mercury(I)"];
  if (halides.includes(an)) {
    return halideExceptions.includes(cat) ? "I" : "S";
  }

  if (an === "Sulfate") {
    const sulfateExceptions = ["Barium", "Lead(II)", "Mercury(I)", "Silver"];
    const slightlyExceptions = ["Calcium", "Strontium"];
    if (sulfateExceptions.includes(cat)) return "I";
    if (slightlyExceptions.includes(cat)) return "sS";
    return "S";
  }

  if (an === "Hydroxide") {
    if (cat === "Barium") return "S";
    if (cat === "Strontium" || cat === "Calcium") return "sS";
    return "I";
  }

  if (an === "Sulfide") {
    const solubleSulfides = ["Magnesium", "Calcium", "Strontium", "Barium"];
    if (solubleSulfides.includes(cat)) return "sS";
    return "I";
  }

  const insolubleAnions = ["Carbonate", "Phosphate", "Chromate", "Oxalate", "Sulfite", "Oxide", "Hydrogen phosphate"];
  if (insolubleAnions.includes(an)) return "I";

  if (an === "Fluoride") {
    const fluorideInsol = ["Magnesium", "Calcium", "Strontium", "Barium", "Lead(II)"];
    if (fluorideInsol.includes(cat)) return "I";
    return "S";
  }

  if (an === "Cyanide") return "I";

  if (["Permanganate", "Chlorate", "Dichromate"].includes(an)) return "S";

  return "S";
}
